/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __classPrivateFieldGet(receiver, privateMap) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to get private field on non-instance");
    }
    return privateMap.get(receiver);
}

function __classPrivateFieldSet(receiver, privateMap, value) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to set private field on non-instance");
    }
    privateMap.set(receiver, value);
    return value;
}

var _createArgRegExp, _database, _language, _loaded, _locale, _logger, _observers, _warning;
class I18n {
    constructor(options) {
        _createArgRegExp.set(this, (arg) => !arg ? /{[^}]*}/g : new RegExp(`{${arg}}`, 'g'));
        _database.set(this, void 0);
        _language.set(this, void 0);
        _loaded.set(this, void 0);
        _locale.set(this, void 0);
        _logger.set(this, void 0);
        _observers.set(this, new Map());
        _warning.set(this, void 0);
        if (options.createArgRegExp)
            __classPrivateFieldSet(this, _createArgRegExp, options.createArgRegExp);
        __classPrivateFieldSet(this, _language, options.language);
        __classPrivateFieldSet(this, _locale, options.locale);
        __classPrivateFieldSet(this, _logger, options.logger || (() => { }));
        __classPrivateFieldSet(this, _warning, !!options.warning);
        __classPrivateFieldSet(this, _database, this.locales.reduce((acc, locale) => (Object.assign(Object.assign({}, acc), { [locale]: {} })), {}));
        __classPrivateFieldSet(this, _loaded, this.locales.reduce((acc, locale) => (Object.assign(Object.assign({}, acc), { [locale]: false })), {}));
    }
    get locales() {
        return Object.keys(__classPrivateFieldGet(this, _language));
    }
    get locale() {
        return __classPrivateFieldGet(this, _locale);
    }
    get t() {
        return this._translate.bind(this);
    }
    exists(key) {
        return this._lookup(key) !== undefined;
    }
    init(data) {
        for (const locale of Object.keys(data))
            __classPrivateFieldGet(this, _language)[locale] = Object.assign(Object.assign({}, data[locale]), __classPrivateFieldGet(this, _language)[locale]);
    }
    isLoaded(locale) {
        return __classPrivateFieldGet(this, _loaded)[locale];
    }
    load(locale, partial) {
        __classPrivateFieldGet(this, _database)[locale] = partial;
        __classPrivateFieldGet(this, _loaded)[locale] = true;
    }
    on(event, observable) {
        const current = __classPrivateFieldGet(this, _observers).get(event) || [];
        __classPrivateFieldGet(this, _observers).set(event, [...current, observable]);
        return () => {
            const current = __classPrivateFieldGet(this, _observers).get(event) || [];
            const filtered = current.filter(o => o !== observable);
            __classPrivateFieldGet(this, _observers).set(event, filtered);
        };
    }
    setLocale(locale) {
        __classPrivateFieldSet(this, _locale, locale);
        this._emit('locale');
    }
    unload(locale) {
        const locales = locale ? [locale] : this.locales;
        for (const current of locales) {
            __classPrivateFieldGet(this, _database)[current] = {};
            __classPrivateFieldGet(this, _loaded)[current] = false;
        }
    }
    _emit(event) {
        let value = undefined;
        if (event === 'locale')
            value = __classPrivateFieldGet(this, _locale);
        const observers = __classPrivateFieldGet(this, _observers).get(event) || [];
        for (const observer of observers)
            observer(value);
    }
    _lookup(key) {
        const k = typeof key === 'string' ? key : key ? 'true' : 'false';
        const database = __classPrivateFieldGet(this, _database)[__classPrivateFieldGet(this, _locale)];
        const language = __classPrivateFieldGet(this, _language)[__classPrivateFieldGet(this, _locale)];
        return database[k] == null ? language[k] : database[k];
    }
    _translate(key, args = {}) {
        // Show warning on missing keys, note that the boolean value false can be a
        // valid result from lookup. This needs an explicit check for undefined.
        const lookup = this._lookup(key);
        // Log on missing key
        if (lookup === undefined) {
            __classPrivateFieldGet(this, _logger).call(this, key, args);
            if (__classPrivateFieldGet(this, _warning))
                console.warn('[i18n] missing key: ' + key);
        }
        // Default to empty string
        const base = lookup === undefined ? '' : lookup;
        if (typeof base !== 'string')
            return base;
        // Log on missing arguments
        const regex = __classPrivateFieldGet(this, _createArgRegExp).call(this);
        const expectedMatch = base.match(regex) || [];
        const expectedArgs = expectedMatch.map(arg => arg.slice(1, -1));
        if (expectedArgs.some(arg => !(arg in args))) {
            __classPrivateFieldGet(this, _logger).call(this, key, args, base);
            if (__classPrivateFieldGet(this, _warning))
                console.warn(`[i18n] missing arguments: ${key}`);
        }
        // Resolve arguments
        return Object.keys(args).reduce((acc, arg) => {
            const regex = __classPrivateFieldGet(this, _createArgRegExp).call(this, arg);
            return acc.replace(regex, '' + args[arg]);
        }, base);
    }
}
_createArgRegExp = new WeakMap(), _database = new WeakMap(), _language = new WeakMap(), _loaded = new WeakMap(), _locale = new WeakMap(), _logger = new WeakMap(), _observers = new WeakMap(), _warning = new WeakMap();

export { I18n };
//# sourceMappingURL=index.js.map
